import { makeAutoObservable, autorun } from "mobx";
import { request, getDurationFromAudioFile } from "../utils";

const ENV = import.meta.env.VITE_ENV;
import uploadFile from "../services/FileUploadService";
import AuthStore from "./AuthStore";
import { showErrorToast, showSuccessToast } from "../services/ToastService";

class CurriculumStore {
  constructor() {
    makeAutoObservable(this);

    autorun(() => {
      if (AuthStore.authenticated) this.init();
      else this.clear();
    });
  }

  loading = false;
  initialized = false;
  initializing = false;

  rawCurricula = [];
  get curricula() {
    return this.rawCurricula
      ?.slice()
      ?.sort((a, b) => a?.order - b?.order)
      ?.map(curriculum => {
        const coursesForCurriculum = this.courses
          ?.filter(course => course?.curriculum?.id === curriculum?.id)
          ?.sort((a, b) => a?.order - b?.order);
        const albums = coursesForCurriculum
          ?.map(({ albums }) => albums)
          ?.flat()
          ?.filter(Boolean);
        const courses = this.coursesByCurriculumId[curriculum?.id];
        return { ...curriculum, courses, albums };
      });
  }

  get curriculaById() {
    return this.curricula?.reduce((acc, next) => {
      acc[next?.id] = next;
      return acc;
    }, {});
  }

  get curriculaByCourseId() {
    return this.curricula?.reduce((acc, next) => {
      const courses = next?.courses ?? [];
      for (const course of courses) acc[course?.id] = next;
      return acc;
    }, {});
  }

  rawCourses = [];
  get courses() {
    return this.rawCourses.map(course => ({
      ...course,
      albums: course?.albums?.map(albumId => this.albumsById?.[albumId])?.sort((a, b) => a?.order - b?.order)
    }));
  }

  get coursesById() {
    return this.courses.reduce((acc, next) => {
      acc[next?.id] = next;
      return acc;
    }, {});
  }

  get coursesByCurriculumId() {
    const coursesGroupedByCurriculum = this.courses.reduce((acc, next) => {
      if (acc[next?.curriculum?.id]) acc[next.curriculum.id] = acc[next.curriculum.id].concat(next);
      else acc[next.curriculum.id] = [next];
      return acc;
    }, {});
    const curriculumCourseEntriesWithSortedCourses = Object.entries(coursesGroupedByCurriculum)?.map(
      ([curriculumId, courses]) => [curriculumId, courses?.sort((a, b) => a?.order - b?.order)]
    );
    return Object.fromEntries(curriculumCourseEntriesWithSortedCourses);
  }

  rawAlbums = [];
  loadingAlbumDetails = {};
  rawAlbumDetails = {};

  get albumDetails() {
    const betterAlbumDetailsEntries = Object.entries(this.rawAlbumDetails).map(([albumId, album]) => [
      albumId,
      {
        ...album,
        tracks: album?.tracks
          ?.map(t => ({
            ...t,
            url: `https://${ENV}-lpm-albums.b-cdn.net/${album.id}/${t.id}?m=${t.modified}`
          }))
          ?.sort((a, b) => a?.order - b?.order),
        playlists: album?.playlists?.slice()?.sort((a, b) => a?.sortOrder - b?.sortOrder)
      }
    ]);
    return Object.fromEntries(betterAlbumDetailsEntries);
  }

  get albums() {
    return this.rawAlbums?.map(album => {
      const albumDetails = this.albumDetails[album?.id] ? this.albumDetails[album.id] : {};
      const modified = album?.modified > albumDetails?.modified ? album?.modified : albumDetails?.modified;
      return {
        ...album,
        ...albumDetails,
        modified,
        artwork: `https://${ENV}-lpm-assets.b-cdn.net/artwork/${album.id}?m=${modified}`,
        playlists: albumDetails?.playlists
          ?.filter(playlist => playlist?.order != null)
          ?.sort((a, b) => a?.order - b?.order)
          ?.map(playlist => ({
            ...playlist,
            tracks: playlist?.tracks?.map(trackId => albumDetails?.tracks?.find(t => trackId === t?.id))
          }))
      };
    });
  }

  get albumsById() {
    return this.albums.reduce((acc, next) => {
      acc[next?.id] = next;
      return acc;
    }, {});
  }

  // True when a failed request means "the entity does not exist" (404), as opposed
  // to a network/5xx failure which must not be treated as a missing entity.
  // Amplify v6 throws errors whose status can live in a few different places.
  static isNotFoundError(err) {
    const status = err?.response?.status ?? err?.statusCode ?? err?.status ?? err?.cause?.status;
    if (status === 404) return true;
    // Amplify may surface the raw response/message without a numeric status.
    const message = err?.message || err?.cause?.message || "";
    return /\b404\b|not found/i.test(message);
  }

  // A 404 on a list endpoint means "no items", which is a valid empty state, not
  // an error. Keep whatever was already loaded (returning a fresh [] would wipe
  // data on a background refresh). Any other failure is rethrown so real
  // problems stay visible.
  emptyOn404(list, err) {
    if (CurriculumStore.isNotFoundError(err)) return list ?? [];
    throw err;
  }

  inFlightFetchCurriculaRequest;
  async fetchCurricula() {
    try {
      if (!this.inFlightFetchCurriculaRequest) this.inFlightFetchCurriculaRequest = request.get(`/curricula`);
      const curricula = await this.inFlightFetchCurriculaRequest;
      this.rawCurricula = curricula || [];
      return this.rawCurricula;
    } catch (err) {
      this.rawCurricula = this.emptyOn404(this.rawCurricula, err);
      return this.rawCurricula;
    } finally {
      this.inFlightFetchCurriculaRequest = null;
    }
  }

  inFlightFetchCoursesRequest;
  async fetchCourses() {
    try {
      if (!this.inFlightFetchCoursesRequest) this.inFlightFetchCoursesRequest = request.get(`/courses`);
      const courses = await this.inFlightFetchCoursesRequest;
      this.rawCourses = courses || [];
      return this.rawCourses;
    } catch (err) {
      this.rawCourses = this.emptyOn404(this.rawCourses, err);
      return this.rawCourses;
    } finally {
      this.inFlightFetchCoursesRequest = null;
    }
  }

  inFlightFetchAlbumListRequest;
  async fetchAlbumList() {
    try {
      if (!this.inFlightFetchAlbumListRequest) this.inFlightFetchAlbumListRequest = request.get(`/albums`);
      const albums = await this.inFlightFetchAlbumListRequest;
      this.rawAlbums = albums || [];
      return this.rawAlbums;
    } catch (err) {
      this.rawAlbums = this.emptyOn404(this.rawAlbums, err);
      return this.rawAlbums;
    } finally {
      this.inFlightFetchAlbumListRequest = null;
    }
  }

  // Resolves to the album on success, or `null` when the album genuinely does not
  // exist (404 / empty payload). Any other failure is rethrown so callers never
  // mistake an error for a missing album and redirect away.
  async fetchAlbumDetails(albumId) {
    this.loadingAlbumDetails = { ...this.loadingAlbumDetails, [albumId]: true };
    try {
      const album = await request.get(`/albums/${albumId}`);
      // A response without an id is not a usable album (some 404s resolve with an
      // empty/error body instead of throwing) - treat it as missing.
      if (!album || typeof album !== "object" || !album.id) return null;
      this.rawAlbumDetails = { ...this.rawAlbumDetails, [albumId]: album };
      return album;
    } catch (err) {
      if (CurriculumStore.isNotFoundError(err)) return null;
      throw err;
    } finally {
      this.loadingAlbumDetails = { ...this.loadingAlbumDetails, [albumId]: false };
    }
  }

  // Shared in-flight promise so concurrent callers (autorun + detail hooks on a
  // cold load) await the same initialization instead of racing it.
  //
  // init() itself never rejects: it records whether initialization succeeded and
  // leaves `initialized` false on failure so a later call can retry. Callers that
  // need to distinguish success from failure use ensureInitialized(), which
  // throws on a still-uninitialized store.
  initPromise = null;
  async init() {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initializing = true;
    this.loading = true;
    this.initPromise = (async () => {
      const results = await Promise.allSettled([
        this.fetchCurricula(),
        this.fetchCourses(),
        this.fetchAlbumList()
      ]);
      const failed = results.some(result => {
        if (result.status === "rejected") {
          console.error(result.reason);
          return true;
        }
        return false;
      });
      if (!failed) this.initialized = true;
      this.loading = false;
      this.initializing = false;
      this.initPromise = null;
    })();

    return this.initPromise;
  }

  // Awaitable guard used by detail hooks: guarantees a single, completed
  // initialization before the hook decides whether an entity is missing.
  //
  // Throws when initialization failed (e.g. a 5xx/network error on a list
  // fetch), so callers treat it as an error rather than concluding the entity
  // does not exist and redirecting the user away.
  async ensureInitialized() {
    if (this.initialized) return;
    await this.init();
    if (!this.initialized) throw new Error("CurriculumStore failed to initialize");
  }

  async addCurriculum(title) {
    try {
      const newCurriculum = await request.post(`/curricula`, { body: { title, order: this.curricula?.length + 1 } });
      this.rawCurricula = this.rawCurricula?.concat(newCurriculum);
      return newCurriculum;
    } catch (err) {
      console.warn(err);
    }
  }

  async deleteCurriculum(curriculumId) {
    try {
      await request.delete(`/curricula/${curriculumId}`);
      this.rawCurricula = this.rawCurricula?.filter(c => c?.id !== curriculumId);
    } catch (err) {
      console.warn(err);
    }
  }

  async addCourse({ curriculum: fullCurriculum, name }) {
    try {
      const { albums, courses, created, modified, entity, order: _, ...curriculum } = fullCurriculum || {};
      const currentCoursesInCurriculum = this.curriculaById?.[curriculum?.id]?.courses || [];
      const order = currentCoursesInCurriculum.length + 1;
      const newCourse = await request.post(`/courses`, { body: { curriculum, name, order } });
      await this.fetchCurricula();
      this.rawCourses = this.rawCourses?.concat(newCourse);
      return newCourse;
    } catch (err) {
      console.warn(err);
    }
  }

  async deleteCourse({ curriculumId, courseId }) {
    try {
      await request.delete(`/courses/${courseId}?curriculumId=${curriculumId}`);
      this.rawCourses = this.rawCourses?.filter(c => c?.id !== courseId);
      await this.fetchCurricula();
    } catch (err) {
      console.warn(err);
    }
  }

  async addAlbum({ curriculumId, courseId, title, artworkFile }) {
    try {
      const currentAlbumsInCourse = this.coursesById?.[courseId]?.albums || [];
      const order = currentAlbumsInCourse.length + 1;
      const newAlbum = await request.post(`/albums`, { body: { curriculumId, courseId, title, order } });
      this.rawAlbums = this.rawAlbums?.concat(newAlbum);
      await this.fetchAlbumDetails(newAlbum?.id);
      await this.updateAlbumArtwork(newAlbum?.id, artworkFile);
      await this.fetchCurricula();
      await this.fetchCourses();
      return newAlbum;
    } catch (err) {
      console.warn(err);
    }
  }

  async updateAlbumArtwork(albumId, file) {
    if (file) {
      try {
        await uploadFile(file, "artwork", `albumId=${albumId}`);
        const updatedAlbum = await request.put(`/albums/${albumId}`, { body: {} });
        this.rawAlbumDetails = { ...this.rawAlbumDetails, [albumId]: updatedAlbum };
        this.rawAlbums = this.rawAlbums?.map(a => (a?.id === albumId ? { ...a, ...updatedAlbum } : a));
        await this.fetchCourses();
        return true;
      } catch {
        return false;
      }
    }
  }

  async updateAlbum(albumId, updates) {
    try {
      const updatedAlbum = await request.put(`/albums/${albumId}`, { body: updates });
      this.rawAlbumDetails = {
        ...this.rawAlbumDetails,
        [albumId]: { ...this.rawAlbumDetails?.[albumId], ...updatedAlbum }
      };
      this.rawAlbums = this.rawAlbums?.map(a => (a?.id === albumId ? { ...a, ...updatedAlbum } : a));
      return updatedAlbum;
    } catch (err) {
      console.warn(err);
      showErrorToast("Error updating album.");
    }
  }

  async deleteAlbum({ curriculumId, courseId, albumId }) {
    try {
      await request.delete(`/albums/${albumId}?curriculumId=${curriculumId}&courseId=${courseId}`);
      await this.fetchCurricula();
      await this.fetchCourses();
      this.rawAlbums = this.rawAlbums?.filter(c => c?.id !== courseId);
      const { albumId: _, ...updatedRawAlbumDetails } = this.rawAlbumDetails;
      this.rawAlbumDetails = updatedRawAlbumDetails;
    } catch (err) {
      console.warn(err);
    }
  }

  async addTrack(albumId, { title, file, principalPurposes, secondaryPurposes }) {
    try {
      const duration = await getDurationFromAudioFile(file);
      const existingTracks = this.albumDetails?.[albumId]?.tracks;
      const order = Math.max(...[0, ...existingTracks?.map(t => t?.order)]) + 1;
      const trackInfo = { title, duration, order, principalPurposes, secondaryPurposes };
      const newTrack = await request.post(`/albums/${albumId}/tracks`, { body: trackInfo });
      await uploadFile(file, "track", `albumId=${albumId}&trackId=${newTrack?.id}`);
      this.rawAlbumDetails = {
        ...this.rawAlbumDetails,
        [albumId]: {
          ...this.rawAlbumDetails?.[albumId],
          tracks: this.rawAlbumDetails?.[albumId]?.tracks?.concat(newTrack)
        }
      };
      showSuccessToast("Track added successfully.");
      return newTrack;
    } catch (err) {
      console.warn(err);
      showErrorToast("Error adding track.");
    }
  }

  async updateTrack(albumId, trackId, updates) {
    try {
      const updatedTrack = await request.put(`/albums/${albumId}/tracks/${trackId}`, { body: updates });
      this.rawAlbumDetails = {
        ...this.rawAlbumDetails,
        [albumId]: {
          ...this.rawAlbumDetails?.[albumId],
          tracks: this.rawAlbumDetails?.[albumId]?.tracks?.map(track => {
            if (track?.id === trackId) return updatedTrack;
            return track;
          })
        }
      };
      return updatedTrack;
    } catch (err) {
      console.warn(err);
    }
  }

  async updateTrackRecording(albumId, trackId, file) {
    if (albumId && trackId && file) {
      try {
        const duration = await getDurationFromAudioFile(file);
        await uploadFile(file, "track", `albumId=${albumId}&trackId=${trackId}`);
        const updatedTrack = await request.put(`/albums/${albumId}/tracks/${trackId}`, { body: { duration } });
        this.rawAlbumDetails = {
          ...this.rawAlbumDetails,
          [albumId]: {
            ...this.rawAlbumDetails?.[albumId],
            tracks: this.rawAlbumDetails?.[albumId]?.tracks?.map(t => {
              return t?.id === trackId ? updatedTrack : t;
            })
          }
        };
        return true;
      } catch {
        return false;
      }
    }
  }

  async deleteTrack(albumId, trackId) {
    try {
      await request.delete(`/albums/${albumId}/tracks/${trackId}`);
      const orderOfTrackToDelete = this.rawAlbumDetails?.[albumId]?.tracks?.find(t => t?.id === trackId)?.order;
      this.rawAlbumDetails = {
        ...this.rawAlbumDetails,
        [albumId]: this.rawAlbumDetails?.[albumId]
          ? {
              ...this.rawAlbumDetails?.[albumId],
              tracks: this.rawAlbumDetails?.[albumId]?.tracks
                ?.map(t => {
                  if (t?.order > orderOfTrackToDelete) return { ...t, order: t?.order - 1 };
                  return t;
                })
                ?.filter(t => t?.id !== trackId)
            }
          : null
      };
      showSuccessToast("Track deleted successfully.");
    } catch (err) {
      console.warn(err);
      showErrorToast("Error deleting track.");
    }
  }

  clear() {
    this.loading = false;
    this.initialized = false;
    this.initializing = false;
    this.initPromise = null;
    this.rawCurricula = [];
    this.rawCourses = [];
    this.rawAlbums = [];
    this.loadingAlbumDetails = {};
    this.rawAlbumDetails = {};
  }
}

export default new CurriculumStore();
